<template>
  <a-select
    v-model:value="localValue"
    placeholder="Select disposition"
    show-search
    @search="handleDispositionSearch"
    @change="handleDispositionChange"
    :open="dropdownOpen"
    @dropdownVisibleChange="handleDropdownVisibleChange"
    :class="selectClass"
  >
    <template #suffixIcon>
      <ThemeImage
        :icon="imageNames.BOTTOM_ARROW"
        alt="bottom_white_arrow"
        class="size-4"
      />
    </template>

    <template #dropdownRender="{ menuNode: menu }">
      <v-nodes :vnodes="menu" />

      <a-divider class="my-2" />
      
      <div
        v-if="!isAddingDisposition"
        class="flex items-center gap-2 cursor-pointer select_vnode rounded-lg hover:bg-[--white-opacity-8]"
        @mousedown.prevent.stop="startAddingDisposition"
      >
        <ThemeImage
          :icon="imageNames.PLUS"
          alt="plus_icon"
          class="size-4"
        />
        <span class="text-sm proxima_nova">Add New disposition</span>
      </div>

      <div v-else class="px-2" @mousedown.prevent @click.stop>
        <CustomInput
          v-model:model-value="newDisposition"
          placeholder="Press enter to create new"
          @keydown.enter.stop.prevent="handleAddDisposition"
          @blur="handleCancelAddDisposition"
          auto-focus
        />
      </div>

    </template>

    <a-select-option
      v-for="disposition in dispositionOptions"
      :key="disposition.id"
      :value="disposition.disposition"
    >
      <div 
        class="flex items-center justify-between w-full pr-2"
        @mouseenter="hoveredDisposition = disposition.id"
        @mouseleave="hoveredDisposition = null"
      >
        <span>{{ disposition.disposition }}</span>
        <ThemeImage
          v-show="dropdownOpen && hoveredDisposition === disposition.id"
          :icon="imageNames.DELETE_GREY"
          alt="delete_icon"
          class="size-4 cursor-pointer delete-icon"
          @mousedown.stop.prevent="() => openDeleteModal(disposition)"
          @click.stop
        />
      </div>
    </a-select-option>
  </a-select>

  <teleport to="body">
    <CustomModal
      v-model:visible="showDeleteModal"
      title="Delete disposition?"
      width="33vw"
      position="top"
    >
      <div class="flex flex-col gap-3">
        <span class="text-base text_color leading-5">
          Are you sure you want to delete this call disposition?
        </span>
        <span class="text-sm secondary_color">
          Note: Deleting this disposition removes it from the workspace for everyone
        </span>
      </div>
      <template #footer>
        <div class="flex gap-2">
          <a-button class="secondary_medium" @click="showDeleteModal = false">
            Cancel
          </a-button>
          <a-button
            class="primary_medium"
            :loading="isDeleting"
            @click="confirmDelete"
          >
            {{ isDeleting ? "Deleting.." : "Delete" }}
          </a-button>
        </div>
      </template>
    </CustomModal>
  </teleport>
</template>

<script setup>
import { ref, watch, onMounted, defineComponent, computed, nextTick } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { 
  fetchCallDispositions, 
  createCallDisposition, 
  deleteCallDisposition, 
  updateCallDisposition 
} from "@/src/utils/callsUtils.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import debounce from "lodash/debounce.js";

const VNodes = defineComponent({
  props: {
    vnodes: { type: Object, required: true },
  },
  render() {
    return this.vnodes;
  },
});

const props = defineProps({
  modelValue: { type: String, default: '' },
  callId: { type: String, default: null },
  selectClass: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const localValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const dispositionOptions = ref([]);
const newDisposition = ref('');
const isAddingDisposition = ref(false);
const hoveredDisposition = ref(null);
const dropdownOpen = ref(false);
const showDeleteModal = ref(false);
const isDeleting = ref(false);
const dispositionToDelete = ref(null);
const preventDropdownClose = ref(false);

const debouncedFetchDispositions = debounce(async (search) => {
  await fetchDispositions(search);
}, 300);

const handleDispositionSearch = (searchValue) => {
  debouncedFetchDispositions(searchValue || '');
};

const fetchDispositions = async (search = '') => {
  try {
    dispositionOptions.value = await fetchCallDispositions({ search });
  } catch (error) {
    console.error("Error fetching dispositions: ", error);
    dispositionOptions.value = [];
  }
};

const handleDispositionChange = async (value) => {
  if (!props.callId) {
    localValue.value = value;
    return;
  }

  const previousValue = props.modelValue;
  
  try {
    const response = await updateCallDisposition(props.callId, value);
    
    if (response?.success) {
      localValue.value = value;
    } else {
      localValue.value = previousValue;
      message.error("Failed to update disposition");
    }
  } catch (error) {
    localValue.value = previousValue;
    message.error("Failed to update disposition");
  }
};

const scrollDropdownToTop = async () => {
  await nextTick();
  const dropdownElement = document.querySelector('.ant-select-dropdown');
  if (dropdownElement) {
    const scrollableElement = dropdownElement.querySelector('.rc-virtual-list-holder');
    if (scrollableElement) {
      scrollableElement.scrollTop = 0;
    }
  }
};

const startAddingDisposition = () => {
  preventDropdownClose.value = true;
  isAddingDisposition.value = true;
};

const handleCancelAddDisposition = () => {
  isAddingDisposition.value = false;
  newDisposition.value = '';
  preventDropdownClose.value = false;
};

const handleAddDisposition = async () => {
  if (!newDisposition.value.trim()) return;

  const dispositionName = newDisposition.value.trim();
  const exists = dispositionOptions.value.some(d => 
    d.disposition.toLowerCase() === dispositionName.toLowerCase()
  );
  
  if (exists) {
    message.warning('This disposition already exists');
    return;
  }

  const tempDisposition = {
    id: `temp_${Date.now()}`,
    disposition: dispositionName
  };

  const previousDisposition = localValue.value;
  dispositionOptions.value.unshift(tempDisposition);
  localValue.value = dispositionName;
  newDisposition.value = '';
  isAddingDisposition.value = false;
  preventDropdownClose.value = false;

  await scrollDropdownToTop();

  try {
    const disposition = await createCallDisposition(dispositionName);
    
    const index = dispositionOptions.value.findIndex(d => d.id === tempDisposition.id);
    if (index !== -1) {
      dispositionOptions.value[index] = disposition;
    }
    
    if (props.callId) {
      try {
        const response = await updateCallDisposition(props.callId, disposition.disposition);
        if (!response?.success) {
          localValue.value = previousDisposition;
          message.error('Failed to update call disposition');
        }
      } catch (updateError) {
        localValue.value = previousDisposition;
        message.error('Failed to update call disposition');
      }
    }
  } catch (error) {
    dispositionOptions.value = dispositionOptions.value.filter(d => d.id !== tempDisposition.id);
    localValue.value = previousDisposition;
    message.error('Failed to create disposition');
  }
};

const handleDropdownVisibleChange = (visible) => {
  if (!visible && preventDropdownClose.value) {
    return;
  }
  if (!visible && isAddingDisposition.value) {
    handleCancelAddDisposition();
    dropdownOpen.value = false;
    return;
  }
  if (!visible) {
    preventDropdownClose.value = false;
    dropdownOpen.value = false;
    return;
  }

  dropdownOpen.value = visible;
  if (visible) {
    fetchDispositions('');
  }
};

const openDeleteModal = (disposition) => {
  dispositionToDelete.value = disposition;
  preventDropdownClose.value = true;
  dropdownOpen.value = true;
  setTimeout(() => {
    showDeleteModal.value = true;
  }, 50);
};

const confirmDelete = async () => {
  if (!dispositionToDelete.value) return;
  
  try {
    isDeleting.value = true;
    await deleteCallDisposition(dispositionToDelete.value.id);
    
    dispositionOptions.value = dispositionOptions.value.filter(
      d => d.id !== dispositionToDelete.value.id
    );
    if (localValue.value === dispositionToDelete.value.disposition) {
      localValue.value = '';
    }
    message.success('Disposition deleted successfully');
    showDeleteModal.value = false;
    preventDropdownClose.value = false;
  } catch (error) {
    message.error('Failed to delete disposition');
    preventDropdownClose.value = false;
  } finally {
    isDeleting.value = false;
    dispositionToDelete.value = null;
  }
};

watch(showDeleteModal, (newVal) => {
  if (!newVal) {
    preventDropdownClose.value = false;
  }
});

onMounted(() => {
  fetchDispositions();
});
</script>

<style scoped>
:deep(.ant-select-selection-item .delete-icon) {
  display: none !important;
}
</style>

